import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { http, errMsg, googleLogin } from "../lib/api";

const inputCls = "w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#EC5B24] transition-colors";

export const AuthDialog = ({ onAuthed }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const h = () => { setMode("login"); setOpen(true); };
    window.addEventListener("open-login", h);
    return () => window.removeEventListener("open-login", h);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "forgot") {
        await http.post("/auth/forgot-password", { email: f.email });
        toast.success("If an account exists, a reset link has been sent to your email.");
        setMode("login");
      } else {
        const body = mode === "register" ? f : { email: f.email, password: f.password };
        const { data } = await http.post(`/auth/${mode === "register" ? "register" : "login"}`, body);
        onAuthed(data);
        toast.success(`Welcome${data.name ? `, ${data.name.split(" ")[0]}` : ""}!`);
        setOpen(false);
      }
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  const field = (k, props) => (
    <input data-testid={`auth-${k}`} required value={f[k]} className={inputCls}
      onChange={(e) => setF({ ...f, [k]: k === "phone" ? e.target.value.replace(/\D/g, "").slice(0, 10) : e.target.value })} {...props} />
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md" data-testid="auth-dialog">
        <DialogHeader>
          <img src="/logo-full.png" alt="WanderNest Tour and Travel" className="h-16 w-auto mx-auto mb-2" />
          <DialogTitle className="font-heading text-center">
            {mode === "register" ? "Create your account" : mode === "forgot" ? "Reset your password" : "Log in to WanderNest"}
          </DialogTitle>
        </DialogHeader>
        {mode !== "forgot" && (
          <>
            <button data-testid="auth-google-btn" onClick={googleLogin} className="w-full rounded-xl border border-slate-200 hover:border-slate-400 py-2.5 text-sm font-semibold flex items-center justify-center gap-2 transition-colors">
              <img src="https://www.google.com/favicon.ico" alt="" className="h-4 w-4" /> Continue with Google
            </button>
            <div className="flex items-center gap-3 text-xs text-slate-400"><span className="h-px flex-1 bg-slate-200" /> or with email <span className="h-px flex-1 bg-slate-200" /></div>
          </>
        )}
        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && field("name", { placeholder: "Full name", maxLength: 80 })}
          {field("email", { type: "email", placeholder: "Email address", maxLength: 120 })}
          {mode === "register" && field("phone", { inputMode: "numeric", placeholder: "10-digit mobile number" })}
          {mode !== "forgot" && field("password", { type: "password", placeholder: mode === "register" ? "Password (min 8 characters)" : "Password", minLength: mode === "register" ? 8 : 1 })}
          <button data-testid="auth-submit-btn" disabled={busy} className="w-full rounded-xl bg-[#EC5B24] hover:bg-[#D84A15] text-white font-bold py-3 flex items-center justify-center gap-2 transition-colors">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {mode === "register" ? "Create account" : mode === "forgot" ? "Send reset link" : "Log in"}
          </button>
        </form>
        <div className="flex justify-between text-sm">
          {mode === "login" ? (
            <>
              <button data-testid="auth-to-register" onClick={() => setMode("register")} className="font-semibold text-[#EC5B24]">New here? Sign up</button>
              <button data-testid="auth-to-forgot" onClick={() => setMode("forgot")} className="text-slate-500">Forgot password?</button>
            </>
          ) : (
            <button data-testid="auth-to-login" onClick={() => setMode("login")} className="font-semibold text-[#EC5B24]">Already have an account? Log in</button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
